import type { Money } from '@/types/catalogue';

/**
 * Renders a price — BUILD-PLAN §33, §40, §44.
 *
 * Always displays the server-formatted string. The client never formats or
 * computes a price itself: currency placement is locale-dependent, and any
 * client-side arithmetic reintroduces the floating-point error the backend's
 * integer minor units exist to prevent.
 *
 * When a sale price is shown, the original is marked up with <s> and given
 * screen-reader context, so the discount is conveyed by more than colour and
 * strikethrough alone (§40 contrast and semantics).
 */

interface PriceProps {
  price: Money;
  compareAt?: Money | undefined;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-xl',
} as const;

export function Price({ price, compareAt, size = 'md', className = '' }: PriceProps) {
  const onSale = compareAt !== undefined;

  return (
    <span className={`inline-flex items-baseline gap-2 ${SIZES[size]} ${className}`}>
      <span className={onSale ? 'text-danger' : 'text-ink'}>
        {onSale && <span className="sr-only">Sale price: </span>}
        {price.formatted}
      </span>

      {onSale && (
        <s className="text-xs text-muted">
          <span className="sr-only">Was: </span>
          {compareAt.formatted}
        </s>
      )}
    </span>
  );
}
